import React, { useEffect, useState } from "react";
import "./Payouts.scss";
import {
  SearchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import {
  Table,
  Button,
  Input,
  Modal,
  message,
  Card,
  Typography,
  Divider,
  Row,
  Col,
  InputNumber,
  Select,
  Tag,
} from "antd";
import { getApi, postApi } from "../../Utlis/apiService";
import {
  GET_INSTRUCTOR_PAYMENTS,
  UPDATE_INSTRUCTOR_COMMISSION,
  UPDATE_PAYMENT_STATUS,
} from "../../Utlis/apiPath";
import { debounce } from "lodash";
import Loader from "../../Components/Loader/Loader";
import { errorToast, successToast } from "../../Services/ToastHelper";
import { PencilLine } from "lucide-react";

const { Title, Text } = Typography;
const { Option } = Select;

const Payouts = () => {
  const [activeTab, setActiveTab] = useState("1");
  const [paymentData, setPaymentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAscending, setIsAscending] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // States for commission modal
  const [isCommissionModalVisible, setIsCommissionModalVisible] =
    useState(false);
  const [commissionPercentage, setCommissionPercentage] = useState("");
  const [commissionAmount, setCommissionAmount] = useState("");
  const [commissionType, setCommissionType] = useState("percentage"); // 'percentage' or 'amount'
  const [selectedPaymentRecord, setSelectedPaymentRecord] = useState(null);
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toLocaleString("default", { month: "long" })
  );
  const [selectedMonthNumber, setSelectedMonthNumber] = useState(
    new Date().getMonth() + 1
  ); // Current month by default
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Current year by default

  // Month data
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Year data
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 2010 },
    (_, i) => currentYear - i
  );

  const getPageSizeOptions = () => {
    return totalRecords <= 50 ? ["10", "20", "50"] : ["15", "25", "50", "100"];
  };

  // Function to fetch payment data with month parameter
  const fetchPaymentData = async (
    page = 1,
    pageSize = 10,
    searchValue = "",
    monthNumber = null,
    year = null
  ) => {
    setIsLoading(true);
    try {
      const pageIndex1 = page - 1;
      const search1 = searchValue !== undefined ? searchValue : search;
      const monthParam = monthNumber || selectedMonthNumber;
      const yearParam = year || selectedYear;

      const response = await getApi(GET_INSTRUCTOR_PAYMENTS, {
        params: {
          month: monthParam,
          year: yearParam,
        },
      });

      if (response.status === 200) {
        // Map the API response to match the expected format
        const mappedData =
          response.data?.map((item, index) => ({
            key: item.slNo || index,
            slNo: item.slNo,
            instructorId: item.instructorId, // Generate ID if not provided
            firstName: item.instructorName?.split(" ")[0] || "",
            lastName: item.instructorName?.split(" ").slice(1).join(" ") || "",
            instructorName: item.instructorName,
            countryCode: "+91", // Default country code
            mobileNo: item.instructorPhone,
            totalBookings: item.noOfBookings,
            mode: item.sessionMode,
            totalAmount: item.totalRevenue,
            perSessionFee: item.finalPerSessionFee,
            paymentStatus: item.paymentStatus,
          }))?.sort((a, b) => a.slNo - b.slNo) || [];
;

        setPaymentData(mappedData);
        setTotalRecords(mappedData.length);
        setPagination((prev) => ({
          ...prev,
          current: page,
          total: mappedData.length,
        }));
      } else if (response.status === 404) {
        setPaymentData([]);
        setTotalRecords(0);
        console.warn("No payment data found for the given criteria.");
      } else {
        setPaymentData([]);
      }
    } catch (error) {
      setPaymentData([]);
      console.error("Error fetching payment data:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSorting = () => {
    setIsAscending((prevState) => (prevState === "" ? false : !prevState));
  };

  const handleSearch = debounce((value) => {
    setSearchTerm(value);
  }, 300);

  const onSearchChange = (e) => {
    handleSearch(e.target.value);
  };

  const debouncedFetchClasses = debounce((searchValue) => {
    setSearch(searchValue);
    const page = 1;
    setPageIndex(1);
    if (activeTab === "1") {
      fetchPaymentData(
        page,
        pageSize,
        searchValue,
        selectedMonthNumber,
        selectedYear
      );
    }
  }, 500);

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination;
    setPageIndex(1);
    setPageIndex(current);
    setPageSize(pageSize);
    if (activeTab === "1") {
      fetchPaymentData(
        current,
        pageSize,
        search,
        selectedMonthNumber,
        selectedYear
      );
    }
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setPageIndex(1);
    setPagination((prev) => ({ ...prev, current: 1 }));

    if (tabKey === "1") {
      // Instructor Payments tab
      fetchPaymentData(1, 10, "", selectedMonthNumber, selectedYear);
    } else if (tabKey === "2") {
      // Staff Payments tab - empty for now
      setPaymentData([]);
      setTotalRecords(0);
    }
  };

  // Function to show commission modal
  const showCommissionModal = (record) => {
    setSelectedPaymentRecord(record);
    setCommissionPercentage(record.commissionPercentage || "");
    setCommissionAmount(record.commissionAmount || "");
    setCommissionType(record.commissionType || "percentage");
    setIsCommissionModalVisible(true);
  };

  // Function to handle commission modal close
  const handleCommissionModalClose = () => {
    setIsCommissionModalVisible(false);
    setSelectedPaymentRecord(null);
    setCommissionPercentage("");
    setCommissionAmount("");
    setCommissionType("percentage");
  };

  // Function to handle commission update
  const handleCommissionSubmit = async () => {
    if (
      commissionType === "percentage" &&
      (!commissionPercentage ||
        commissionPercentage <= 0 ||
        commissionPercentage > 100)
    ) {
      errorToast("Please enter a valid commission percentage (1-100).");
      return;
    }

    if (
      commissionType === "amount" &&
      (!commissionAmount || commissionAmount <= 0)
    ) {
      errorToast("Please enter a valid commission amount.");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        instructorId: selectedPaymentRecord.instructorId,
        commissionType,
        ...(commissionType === "percentage"
          ? { commissionPercentage: parseFloat(commissionPercentage) }
          : { commissionAmount: parseFloat(commissionAmount) }),
      };

      const { status, message } = await postApi(
        UPDATE_INSTRUCTOR_COMMISSION,
        payload
      );

      if (status === 200) {
        successToast(message);
        fetchPaymentData(pagination.current, pageSize);
        handleCommissionModalClose();
      } else {
        errorToast(message);
      }
    } catch (error) {
      console.error("Error updating commission:", error);
      errorToast("Failed to update commission. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterApply = (month, year) => {
    const monthIndex = months.indexOf(month) + 1;
    setSelectedMonth(month);
    setSelectedMonthNumber(monthIndex);
    setSelectedYear(year);
    setIsFilterModalVisible(false);
  };

  const handleResetFilter = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentMonthName = months[currentMonth - 1];
    const currentYear = currentDate.getFullYear();

    setSelectedMonth(currentMonthName);
    setSelectedMonthNumber(currentMonth);
    setSelectedYear(currentYear);

    // Refresh the data with current month and year
    fetchPaymentData(1, 10, "", currentMonth, currentYear);
  };

  // Function to calculate commission amount based on percentage
  const calculateCommissionAmount = (totalAmount, percentage) => {
    return totalAmount ? ((totalAmount * percentage) / 100).toFixed(2) : 0;
  };

  // Function to calculate amount paid to instructor
  const calculateInstructorAmount = (totalAmount, commissionAmount) => {
    return totalAmount ? (totalAmount - commissionAmount).toFixed(2) : 0;
  };

  // Initialize with current month on component mount
  useEffect(() => {
    const currentMonth = new Date().getMonth() + 1;
    const currentMonthName = months[currentMonth - 1];
    setSelectedMonth(currentMonthName);
    setSelectedMonthNumber(currentMonth);
  }, []);

  // Check if current selection is different from current month/year
  const isFilterActive = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentMonthName = months[currentMonth - 1];
    const currentYear = currentDate.getFullYear();

    return selectedMonth !== currentMonthName || selectedYear !== currentYear;
  };

  useEffect(() => {
    if (activeTab === "1") {
      fetchPaymentData(1, 10, "", selectedMonthNumber, selectedYear);
    }
  }, [isAscending, activeTab, selectedMonthNumber, selectedYear]);

  // Columns for Instructor Payment tab
  const instructorPaymentColumns = [
    {
      title: "SL No",
      dataIndex: "slNo",
      key: "slNo",
    },
    {
      title: "Instructor Name",
      dataIndex: "instructorName",
      key: "instructorName",
    },
    {
      title: "Instructor Phone",
      dataIndex: "mobileNo",
      key: "mobileNo",
      render: (phone, record) => `${record.countryCode || "+91"} ${phone}`,
    },
    {
      title: "Mode",
      dataIndex: "mode",
      key: "mode",
      render: (mode) => (
        <Tag color={mode === "Online" ? "blue" : "green"}>{mode}</Tag>
      ),
    },
    {
      title: "No of Classes Attended",
      dataIndex: "totalBookings",
      key: "totalBookings",
    },
    {
      title: "Per Session Fee",
      dataIndex: "perSessionFee",
      key: "perSessionFee",
      render: (fee) => (fee ? `₹${fee}` : "N/A"),
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (amount) => `₹${amount || 0}`,
    },
    {
      title: "Payment Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      render: (status, record) => {
        const getColor = (value) => {
          if (value === "Completed") return "green";
          if (value === "Pending") return "orange";
          return "red";
        };

        const handleStatusChange = async (nextValue) => {
          setIsLoading(true);
          try {
            const monthParam = selectedMonthNumber;
            const yearParam = selectedYear;
            const body = {
              instructorId: record?.instructorId,
              month: monthParam,
              year: yearParam,
              sessionMode: record.mode, // ✅ fix: was record.mode
            };

            const res = await postApi(UPDATE_PAYMENT_STATUS, body);

            if (res?.status === 200) {
              successToast("Payment status updated");
              fetchPaymentData(pagination.current, pageSize);
            } else {
              errorToast(res?.message || "Failed to update payment status");
            }
          } catch (error) {
            console.error("Error updating status:", error);
            errorToast("Failed to update payment status");
          } finally {
            setIsLoading(false);
          }
        };

        // ✅ If status is "Completed", only show tag
        if (status === "Completed") {
          return <Tag color={getColor(status)}>{status}</Tag>;
        }

        // ✅ Otherwise show dropdown with colored tags
        return (
          <Select
            value={status}
            style={{ width: 140 }}
            onChange={handleStatusChange}
            tagRender={({ value }) => (
              <Tag color={getColor(value)}>{value}</Tag>
            )}
          >
            <Option value="Pending">
              <span style={{ color: "orange" }}>Pending</span>
            </Option>
            <Option value="Completed">
              <span style={{ color: "green" }}>Completed</span>
            </Option>
          </Select>
        );
      },
    },
  ];

  // Staff Payment columns (empty for now)
  const staffPaymentColumns = [
    {
      title: "SL No",
      dataIndex: "slNo",
      key: "slNo",
    },
    {
      title: "Staff Name",
      dataIndex: "staffName",
      key: "staffName",
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "Payment Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      render: (status, record) => {
        const getColor = (value) => {
          if (value === "Paid") return "green";
          if (value === "Pending") return "orange";
          return "red";
        };

        return (
          <Select
            value={status}
            style={{ width: 120 }}
            onChange={(value) => {
              // 🔹 Update status here
              console.log("Updated status:", value, "for record:", record);

              // Example: call API or update state
              // updatePaymentStatus(record.id, value)
            }}
          >
            <Option value="Paid">
              <Tag color="green">Paid</Tag>
            </Option>
            <Option value="Pending">
              <Tag color="orange">Pending</Tag>
            </Option>
            <Option value="Failed">
              <Tag color="red">Failed</Tag>
            </Option>
          </Select>
        );
      },
    },
  ];

  return (
    <div className="payouts">
      {isLoading && <Loader />}

      <div className="payouts_header">
        <h2>Payouts</h2>
      </div>

      <div className="payouts_content">
        <div className="payouts_content_custom">
          <div
            className={activeTab === "1" ? "tab selected" : "tab"}
            onClick={() => handleTabChange("1")}
          >
            Instructor Payments
          </div>
          <div
            className={activeTab === "2" ? "tab selected" : "tab"}
            onClick={() => handleTabChange("2")}
          >
            Staff Payments
          </div>
        </div>

        <div className="payouts_content_search">
          {activeTab === "1" && (
            <>
              <Button
                icon={<CalendarOutlined />}
                onClick={() => setIsFilterModalVisible(true)}
                className="monthFilterButton"
              >
                {selectedMonth}, {selectedYear}
              </Button>
              {isFilterActive() && (
                <Button onClick={handleResetFilter} className="resetButton">
                  Reset
                </Button>
              )}
            </>
          )}

          {/* {activeTab === "1" && (
            <>
              <Input
                placeholder="Search"
                prefix={<SearchOutlined />}
                onChange={(e) => debouncedFetchClasses(e.target.value)}
              />
              <Button
                icon={
                  isAscending === true ? (
                    <SortAscendingOutlined />
                  ) : (
                    <SortDescendingOutlined />
                  )
                }
                className="sortButton"
                onClick={handleSorting}
              />
            </>
          )} */}
        </div>
      </div>

      <div className="payouts_table">
        <Table
          columns={
            activeTab === "1" ? instructorPaymentColumns : staffPaymentColumns
          }
          dataSource={activeTab === "1" ? paymentData : []}
          pagination={{
            pageSize,
            total: totalRecords,
            showSizeChanger: true,
            pageSizeOptions: getPageSizeOptions(),
          }}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
        />
      </div>

      {/* Filter Modal */}
      <FilterModal
        visible={isFilterModalVisible}
        onCancel={() => setIsFilterModalVisible(false)}
        onApply={handleFilterApply}
        months={months}
        years={years}
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
      />

      {/* Commission Modal */}
      <Modal
        title="Set Commission"
        open={isCommissionModalVisible}
        onCancel={handleCommissionModalClose}
        onOk={handleCommissionSubmit}
        okText="Update Commission"
        cancelText="Cancel"
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Commission Type:</Text>
          <Select
            value={commissionType}
            onChange={setCommissionType}
            style={{ width: "100%", marginTop: 8 }}
          >
            <Option value="percentage">Percentage</Option>
            <Option value="amount">Fixed Amount</Option>
          </Select>
        </div>

        {commissionType === "percentage" ? (
          <div>
            <Text strong>Commission Percentage (%):</Text>
            <InputNumber
              min={0}
              max={100}
              value={commissionPercentage}
              onChange={setCommissionPercentage}
              style={{ width: "100%", marginTop: 8 }}
              placeholder="Enter commission percentage"
            />
          </div>
        ) : (
          <div>
            <Text strong>Commission Amount (₹):</Text>
            <InputNumber
              min={0}
              value={commissionAmount}
              onChange={setCommissionAmount}
              style={{ width: "100%", marginTop: 8 }}
              placeholder="Enter commission amount"
            />
          </div>
        )}

        {selectedPaymentRecord && (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              backgroundColor: "#f5f5f5",
              borderRadius: 4,
            }}
          >
            <Text strong>Summary:</Text>
            <div style={{ marginTop: 8 }}>
              <Text>
                Total Amount: ₹
                {selectedPaymentRecord.totalAmount?.toFixed(2) || 0}
              </Text>
            </div>
            {commissionType === "percentage" && commissionPercentage && (
              <div>
                <Text>
                  Commission Amount: ₹
                  {calculateCommissionAmount(
                    selectedPaymentRecord.totalAmount,
                    commissionPercentage
                  )}
                </Text>
              </div>
            )}
            {commissionType === "amount" && commissionAmount && (
              <div>
                <Text>Commission Amount: ₹{commissionAmount}</Text>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

// Filter Modal Component
const FilterModal = ({
  visible,
  onCancel,
  onApply,
  months,
  years,
  selectedMonth,
  selectedYear,
}) => {
  const [month, setMonth] = useState(selectedMonth);
  const [year, setYear] = useState(selectedYear);

  const handleApply = () => {
    onApply(month, year);
  };

  return (
    <Modal
      title="Select Month and Year"
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleApply}>
          Apply
        </Button>,
      ]}
    >
      <div style={{ display: "flex", justifyContent: "space-around" }}>
        <Select
          value={month}
          onChange={(value) => setMonth(value)}
          style={{ width: 150 }}
        >
          {months.map((m) => (
            <Option key={m} value={m}>
              {m}
            </Option>
          ))}
        </Select>
        <Select
          value={year}
          onChange={(value) => setYear(value)}
          style={{ width: 150 }}
        >
          {years.map((y) => (
            <Option key={y} value={y}>
              {y}
            </Option>
          ))}
        </Select>
      </div>
    </Modal>
  );
};

export default Payouts;
